import { BarChart3 } from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router";

import { useSuppliersAccess } from "@/hooks/use-suppliers-access";
import { SpendCentreTabs } from "../components/spend-centre-tabs";
import { UnderlineTab } from "../components/underline-tab";
import { ExpiredContractsReportView } from "./expired-contracts-report";
import { NoContractReportView } from "./no-contract-report";
import { MAX_GRACE_DAYS, TOP_OPTIONS, type TopOption } from "./report-utils";
import type { ReportViewState } from "./types";

const REPORTS = [
  {
    id: "expired",
    label: "Expired contracts",
    title: "Spend on expired contracts",
    description:
      "Spend still coming in on contracts that are past their expiry date and have no active contract sharing the same ID. Each month, spend is totalled per contract so you can see what is being paid after expiry.",
  },
  {
    id: "no-contract",
    label: "No contract",
    title: "Spend with no contract",
    description:
      "Spend on contract IDs that do not exist anywhere in the contracts application, whatever the status. Each month, spend is totalled per ID so you can see what is being paid with no contract in place.",
  },
] as const;
type ReportId = (typeof REPORTS)[number]["id"];

const parseTop = (value: string | null): TopOption =>
  TOP_OPTIONS.includes(value as TopOption) ? (value as TopOption) : "all";

const parseGrace = (value: string | null) => {
  const days = Number(value);
  return Number.isInteger(days) && days >= 0 && days <= MAX_GRACE_DAYS ? days : 0;
};

export const SpendReportsPage = () => {
  // Spend with no contract has no folder to check access against; the API limits it to Spend Centre admins.
  const { isSpendCentreAdmin: isAdmin } = useSuppliersAccess();
  const [params, setParams] = useSearchParams();
  const requested = params.get("report") === "no-contract" && isAdmin ? "no-contract" : "expired";
  const report = REPORTS.find(item => item.id === requested) ?? REPORTS[0];
  const reports = REPORTS.filter(item => item.id === "expired" || isAdmin);
  const state: ReportViewState = {
    month: params.get("month") ?? "",
    search: params.get("q") ?? "",
    top: parseTop(params.get("top")),
  };

  const update = React.useCallback(
    (values: Record<string, string | number | undefined>) =>
      setParams(
        current => {
          const next = new URLSearchParams(current);
          Object.entries(values).forEach(([key, value]) =>
            value === undefined || value === "" ? next.delete(key) : next.set(key, String(value)),
          );
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const onStateChange = (change: Partial<ReportViewState>) =>
    update({
      ...(change.month !== undefined ? { month: change.month } : {}),
      ...(change.search !== undefined ? { q: change.search } : {}),
      ...(change.top !== undefined ? { top: change.top === "all" ? undefined : change.top } : {}),
    });
  const selectReport = (id: ReportId) => update({ report: id === "expired" ? undefined : id, q: undefined });

  return (
    <main
      className="-mx-4 -my-8 min-h-screen bg-[#f7f3ee] px-5 py-7 sm:-mx-6 lg:-mx-8 lg:px-8"
      aria-label="Spend Centre reports"
    >
      <div className="mx-auto max-w-7xl">
        <header>
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-desert-sand-700">
            <BarChart3 className="size-4" /> Supplier intelligence
          </div>
          <h1 className="text-3xl font-bold text-[#111b35]">Spend Centre</h1>
          <SpendCentreTabs active="reports" />
        </header>
        <section className="mt-6" aria-labelledby="spend-report-title">
          <h2 id="spend-report-title" className="text-2xl font-semibold text-[#111b35]">
            {report.title}
          </h2>
          <p className="mt-2 max-w-4xl text-sm text-gray-600">{report.description}</p>
          <div role="tablist" aria-label="Reports" className="mt-5 flex gap-1 border-b border-spend-divider">
            {reports.map(item => (
              <UnderlineTab
                key={item.id}
                active={item.id === report.id}
                role="tab"
                aria-selected={item.id === report.id}
                onClick={() => selectReport(item.id)}
              >
                {item.label}
              </UnderlineTab>
            ))}
          </div>
          <div role="tabpanel" aria-label={report.label} className="mt-5">
            {report.id === "expired" ? (
              <ExpiredContractsReportView
                state={state}
                onStateChange={onStateChange}
                graceDays={parseGrace(params.get("grace"))}
                onGraceDaysChange={grace => update({ grace: grace || undefined })}
              />
            ) : (
              <NoContractReportView state={state} onStateChange={onStateChange} />
            )}
          </div>
        </section>
      </div>
    </main>
  );
};
